import { useEffect, useMemo, useRef, useState } from 'react';
import BoltIcon from '@mui/icons-material/Bolt';
import { ClickAwayListener, Popper, Paper } from '@mui/material';
import { useQuickReplies } from '@layouts/pages/Settings/quick-replies/hooks/use-quick-replies.hook';
import {
    Box, TextField, IconButton, Typography, Skeleton, Alert, CircularProgress, Link, Chip,
    Tooltip, Avatar, InputAdornment,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ForumIcon from '@mui/icons-material/Forum';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ReplyIcon from '@mui/icons-material/Reply';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { useTranslation } from 'react-i18next';
import { useMaterialUIController } from 'context';
import MDButton from '@components/MDButton';
import { useAlert } from '@components/showAlert/useAlertHook';
import { apiErrorMessage } from '@functions/api-error-message';
import { formatPhone } from '@functions/format-phone';
import { MessageStatusIcon } from '@components/messages/message-status-icon';
import { fillLeadVariables } from '@components/MessageField/fill-lead-variables';
import { authStorage } from '@functions/auth-session';
import { SOURCE_KEY } from '@layouts/pages/CRM/kanban-board/formatters/lead-source-label';
import { ELeadSource } from '@layouts/pages/CRM/kanban-board/services/responses/lead.response';
import { ACCEPT_ATTR, validateAttachment } from '@components/messages/upload-limits';
import { getCrmColors, inkOver } from '../../CRM/kanban-board/crm-theme';
import {
    formatCrmDayLabel, formatCrmTime, sameGroupingWindow, sameDay,
} from '../../CRM/kanban-board/formatters/crm-datetime';
import { resolve } from './channel-identity';
import { TemplateDialog } from './template-dialog';
import { MultichannelService } from '../services/multichannel.service';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { AiService, useAi } from '@services/ai.service';
import { MessageAudio } from './message-audio';
import MicIcon from '@mui/icons-material/Mic';
import { recordingClock } from '@functions/voice-recording';
import { useVoiceRecorder } from '../hooks/use-voice-recorder.hook';
import {
    MultichannelConversation,
    MultichannelLead,
    MultichannelMessage,
} from '../services/responses/multichannel.response';

interface Props {
    conversation: MultichannelConversation | null;
    /// O lead da conversa, quando existe: de onde saem os valores das variáveis da resposta rápida.
    lead?: MultichannelLead | null;
    /// Incrementa quando chega mensagem nova nesta conversa.
    signal: number;
    onSent: () => void;
}

/// Mensagens seguidas do mesmo remetente são uma fala só: evita repetir nome e horário.
interface MessageGroup {
    messages: MultichannelMessage[];
    outgoing: boolean;
    senderName?: string | null;
    isPrivate: boolean;
    startsDay: boolean;
}

function groupMessages(messages: MultichannelMessage[]): MessageGroup[] {
    const groups: MessageGroup[] = [];

    messages.forEach((message) => {
        const lastGroup = groups[groups.length - 1];
        const last = lastGroup?.messages[lastGroup.messages.length - 1];

        // Nota interna nunca entra no grupo de mensagem que o contato recebeu: o visual difere.
        const continues = last !== undefined
            && lastGroup.outgoing === message.outgoing
            && lastGroup.isPrivate === message.private
            && (lastGroup.senderName ?? null) === (message.senderName ?? null)
            && sameDay(last.createdAt, message.createdAt)
            && sameGroupingWindow(last.createdAt, message.createdAt);

        if (continues) {
            lastGroup.messages.push(message);
            return;
        }

        groups.push({
            messages: [message],
            outgoing: message.outgoing,
            senderName: message.senderName,
            isPrivate: message.private,
            startsDay: last === undefined
                || !sameDay(last.createdAt, message.createdAt),
        });
    });

    return groups;
}

/// O canto reto marca a continuação do grupo; o arredondado, onde a fala começa e termina.
function corners(outgoing: boolean, first: boolean, last: boolean): string {
    const full = '12px';
    const tight = '4px';

    return outgoing
        ? `${full} ${first ? full : tight} ${last ? full : tight} ${full}`
        : `${first ? full : tight} ${full} ${full} ${last ? full : tight}`;
}

export const MultichannelConversationPanel = ({ conversation, lead, signal, onSent }: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const { showAlert, alertDelete } = useAlert();
    const service = new MultichannelService();

    const [messages, setMessages] = useState<MultichannelMessage[] | null>(null);
    const [text, setText] = useState('');
    const ai = useAi();
    const aiConfigured = ai?.configured ?? false;
    const [suggesting, setSuggesting] = useState(false);

    /// A sugestão só preenche o campo (quem manda é o atendente); texto já escrito é substituído.
    const suggestReply = async () => {
        if (!conversation) return;

        setSuggesting(true);

        try {
            const res = await new AiService().suggestReply(conversation.id, conversation.inboxId);
            if (res.data.data?.text) setText(res.data.data.text);
            inputRef.current?.focus();
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 5000);
        } finally {
            setSuggesting(false);
        }
    };
    const { quickReplies } = useQuickReplies();
    const [openReplies, setOpenReplies] = useState(false);
    const fieldRef = useRef<HTMLDivElement | null>(null);

    /// O campo de texto; campoRef é a raiz do TextField, que ancora o menu de respostas rápidas.
    const inputRef = useRef<HTMLTextAreaElement | null>(null);

    // Só sugere enquanto a mensagem inteira é o atalho sendo digitado.
    const noSpace = ![...text].some((c) => c.trim() === '');
    const typedShortcut = text.startsWith('/') && noSpace ? text.toLowerCase() : null;

    const suggestions = typedShortcut === null
        ? []
        : quickReplies.filter((r) => r.shortcut.toLowerCase().startsWith(typedShortcut));

    /// Entra preenchida: nenhum servidor mexe depois, e o que sobrar chegaria ao cliente com a chave.
    /// Preencher na inserção, e não no envio, também deixa o atendente revisar.
    const applyReply = (content: string) => {
        setText(fillLeadVariables(content, {
            name: lead?.contactName ?? conversation?.contactName,
            phone: lead?.contactPhone,
            email: lead?.contactEmail,
            status: lead?.statusName,
            owner: lead?.assignedUserName,
            source: lead ? t(SOURCE_KEY[lead.source as ELeadSource]) : null,
            agent: authStorage.read()?.name,
        }));
        setOpenReplies(false);
    };
    const [attachment, setAttachment] = useState<File | null>(null);
    const [replyingTo, setReplyingTo] = useState<MultichannelMessage | null>(null);
    const [templatesOpen, setTemplatesOpen] = useState(false);
    const [sending, setSending] = useState(false);
    const endOfList = useRef<HTMLDivElement | null>(null);
    const fileField = useRef<HTMLInputElement | null>(null);

    const conversationId = conversation?.id;
    const voice = useVoiceRecorder();

    const groups = useMemo(() => groupMessages(messages ?? []), [messages]);

    // Trocar rápido de conversa dispara cargas em sequência: a resposta antiga não pode cobrir a nova.
    const requestRef = useRef(0);

    const load = async () => {
        if (!conversationId) return;
        const request = ++requestRef.current;
        try {
            const reply = await service.messages(conversationId);
            if (request === requestRef.current) setMessages(reply.data.data ?? []);
        } catch (err) {
            if (request !== requestRef.current) return;
            showAlert(apiErrorMessage(err), 'error', 3000);
            setMessages([]);
        }
    };

    useEffect(() => {
        setMessages(null);
        setText('');
        setAttachment(null);
        setReplyingTo(null);
        load();
    }, [conversationId]);

    // Gravação de uma conversa não vai parar em outra.
    useEffect(() => () => voice.cancel(), [conversationId, voice.cancel]);

    const startVoice = async () => {
        const error = await voice.start();

        if (error) {
            showAlert(
                t(error === 'negado' ? 'crmMultichannel.microfoneNegado' : 'crmMultichannel.microfoneFalhou'),
                'error',
                5000);
        }
    };

    /// A nota de voz vai como anexo de áudio, pelo mesmo caminho do arquivo.
    const sendVoice = async () => {
        if (!conversationId || sending) return;

        setSending(true);

        // A conversão para OGG roda no navegador; falhar nela é falha de envio, com recado próprio.
        let file: File | null;

        try {
            file = await voice.stop();
        } catch {
            showAlert(t('crmMultichannel.audioNaoConverteu'), 'error', 5000);
            setSending(false);
            return;
        }

        if (!file) {
            setSending(false);
            return;
        }

        try {
            await service.sendMedia(conversationId, file);
            await load();
            onSent();
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 5000);
        } finally {
            setSending(false);
        }
    };

    // Recarrega sem zerar a lista, para a conversa não piscar em esqueleto a cada mensagem.
    useEffect(() => {
        if (signal > 0) load();
    }, [signal]);

    useEffect(() => {
        endOfList.current?.scrollIntoView({ block: 'end' });
    }, [messages]);

    // Validar aqui evita subir o arquivo inteiro para receber a recusa no fim.
    const chooseFile = (file?: File | null) => {
        if (!file) return;

        const problem = validateAttachment(file);

        if (problem === 'tamanho') {
            showAlert(t('crmMessages.anexoGrande'), 'error', 4000);
            return;
        }

        if (problem === 'formato') {
            showAlert(t('crmMessages.anexoFormato'), 'error', 4000);
            return;
        }

        setAttachment(file);
    };

    const remove = (message: MultichannelMessage) => {
        if (!conversationId) return;

        // O aviso vai no texto da confirmação: quem apaga precisa saber que o contato já recebeu.
        alertDelete(String(message.id), async () => {
            try {
                await service.deleteMessage(conversationId, message.id);
                await load();
                onSent();
            } catch (err) {
                showAlert(apiErrorMessage(err), 'error', 4000);
            }
        }, undefined, t('crmMultichannel.excluirAviso'));
    };

    const send = async () => {
        if (!conversationId || sending) return;
        if (!attachment && text.trim().length === 0) return;

        // O campo esvazia agora, não na resposta; se o envio falhar, o texto volta.
        const content = text.trim();
        const attachmentSent = attachment;
        const quoted = replyingTo;

        setText('');
        setAttachment(null);
        setReplyingTo(null);
        setSending(true);
        inputRef.current?.focus();

        try {
            if (attachmentSent) {
                await service.sendMedia(conversationId, attachmentSent, content || undefined);
            } else {
                await service.send(conversationId, content, quoted?.id);
            }

            await load();
            onSent();
        } catch (err) {
            setText((current) => (current.length === 0 ? content : current));
            setAttachment((current) => current ?? attachmentSent);
            setReplyingTo((current) => current ?? quoted);
            showAlert(apiErrorMessage(err), 'error', 4000);
        } finally {
            setSending(false);
            inputRef.current?.focus();
        }
    };

    if (!conversation) {
        return (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box sx={{ textAlign: 'center' }}>
                    <ForumIcon sx={{ fontSize: 44, color: cc.textMuted, mb: 1 }} />
                    <Typography variant="body2" sx={{ color: cc.textSecondary }}>
                        {t('crmMultichannel.selecione')}
                    </Typography>
                </Box>
            </Box>
        );
    }

    // Quem decide se dá para responder é o Chatwoot, que conhece a regra de cada canal.
    const blocked = !conversation.canReplyFreely;

    // Campo vazio: o botão de enviar vira microfone, como no WhatsApp.
    const canRecord = voice.supported && !blocked && !attachment && text.trim().length === 0;

    const identification = conversation.contactPhone
        ? formatPhone(conversation.contactPhone)
        : conversation.contactEmail ?? '';

    const channel = resolve(conversation.channel);
    const contactName = conversation.contactName || `#${conversation.id}`;

    // Template aprovado é do WhatsApp: nos outros canais o Chatwoot aceita o envio e não entrega.
    const acceptsTemplate = (conversation.channel ?? '')
        .toLowerCase()
        .includes('whatsapp');

    const initials = contactName
        .split(' ').filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('');

    return (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <Box
                sx={{
                    p: '11px 16px',
                    borderBottom: `1px solid ${cc.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.4,
                }}
            >
                <Avatar
                    sx={{
                        width: 36,
                        height: 36,
                        fontSize: 12.5,
                        fontWeight: 700,
                        bgcolor: conversation.leadStatusColor || cc.countBg,
                        color: conversation.leadStatusColor
                            ? inkOver(conversation.leadStatusColor)
                            : cc.textSecondary,
                        border: conversation.leadStatusColor
                            ? 'none'
                            : `1px dashed ${cc.borderStrong}`,
                    }}
                >
                    {initials}
                </Avatar>

                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" fontWeight={700} noWrap sx={{ color: cc.textPrimary }}>
                        {contactName}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                            <channel.Icon sx={{ fontSize: 11, color: cc.textSecondary }} />
                            <Typography
                                variant="caption"
                                sx={{ fontSize: '11px', fontWeight: 500, color: cc.textSecondary }}
                            >
                                {channel.label}
                            </Typography>
                        </Box>

                        {identification && (
                            <>
                                <Typography variant="caption" sx={{ color: cc.textMuted }}>·</Typography>
                                <Typography
                                    variant="caption"
                                    sx={{ fontSize: '11px', color: cc.textSecondary }}
                                >
                                    {identification}
                                </Typography>
                            </>
                        )}

                        {conversation.leadColumnName && (
                            <>
                                <Typography variant="caption" sx={{ color: cc.textMuted }}>·</Typography>
                                <Typography
                                    variant="caption"
                                    sx={{ fontSize: '11px', color: cc.textSecondary }}
                                >
                                    {conversation.leadColumnName}
                                </Typography>
                            </>
                        )}

                        {conversation.leadStatusName && (
                            <>
                                <Typography variant="caption" sx={{ color: cc.textMuted }}>·</Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontSize: '11px',
                                        fontWeight: 700,
                                        color: conversation.leadStatusColor || cc.textSecondary,
                                    }}
                                >
                                    {conversation.leadStatusName}
                                </Typography>
                            </>
                        )}
                    </Box>
                </Box>
            </Box>

            <Box
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    bgcolor: cc.threadBg,
                    p: '18px 20px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }}
            >
                {!messages ? (
                    Array.from({ length: 4 }).map((_, index) => (
                        <Skeleton key={index} height={52} variant="rounded" />
                    ))
                ) : messages.length === 0 ? (
                    <Typography
                        variant="caption"
                        sx={{ textAlign: 'center', py: 4, color: cc.textSecondary }}
                    >
                        {t('crmMultichannel.semMensagens')}
                    </Typography>
                ) : (
                    groups.map((group) => {
                        const lastOfGroup = group.messages[group.messages.length - 1];

                        return (
                            <Box
                                key={`g-${group.messages[0].id}`}
                                sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                            >
                                {group.startsDay && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                bgcolor: cc.countBg,
                                                color: cc.textSecondary,
                                                fontSize: '10.5px',
                                                fontWeight: 700,
                                                letterSpacing: '.06em',
                                                textTransform: 'uppercase',
                                                px: 1.4,
                                                py: 0.5,
                                                borderRadius: '11px',
                                            }}
                                        >
                                            {formatCrmDayLabel(group.messages[0].createdAt)}
                                        </Typography>
                                    </Box>
                                )}

                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: group.outgoing ? 'flex-end' : 'flex-start',
                                        gap: 0.4,
                                    }}
                                >
                                    {group.outgoing && group.senderName && (
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontSize: '10.5px',
                                                color: cc.textMuted,
                                                px: 0.4,
                                                mb: 0.2,
                                            }}
                                        >
                                            {group.senderName}
                                        </Typography>
                                    )}

                                    {group.messages.map((message, index) => {
                                        const quoted = message.inReplyTo
                                            ? messages.find((m) => m.id === message.inReplyTo)
                                            : undefined;

                                        const first = index === 0;
                                        const last = index === group.messages.length - 1;

                                        const background = message.private
                                            ? `${cc.eventWarning}1a`
                                            : message.outgoing
                                                ? cc.bubbleOutgoing
                                                : cc.bubbleIncoming;

                                        return (
                                            <Box
                                                key={message.id}
                                                sx={{
                                                    alignSelf: group.outgoing
                                                        ? 'flex-end'
                                                        : 'flex-start',
                                                    display: 'flex',
                                                    flexDirection: group.outgoing
                                                        ? 'row-reverse'
                                                        : 'row',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    maxWidth: '78%',
                                                    minWidth: 0,
                                                    '&:hover .acoes-da-mensagem': { opacity: 1 },
                                                }}
                                            >
                                                {/* O balão volta à largura do conteúdo: horário e nome foram para o rodapé do grupo. */}
                                                <Box
                                                    sx={{
                                                        minWidth: 0,
                                                        bgcolor: background,
                                                        border: message.private
                                                            ? `1px dashed ${cc.eventWarning}`
                                                            : 'none',
                                                        borderRadius: corners(
                                                            group.outgoing, first, last),
                                                        p: '9px 13px',
                                                    }}
                                                >
                                                    {message.private && (
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 0.4,
                                                                mb: 0.5,
                                                            }}
                                                        >
                                                            <LockOutlinedIcon
                                                                sx={{ fontSize: 12, color: cc.eventWarning }}
                                                            />
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    color: cc.eventWarning,
                                                                    fontWeight: 700,
                                                                    fontSize: '10px',
                                                                }}
                                                            >
                                                                {t('crmMultichannel.notaInterna')}
                                                            </Typography>
                                                        </Box>
                                                    )}

                                                    {quoted && (
                                                        <Box
                                                            sx={{
                                                                borderLeft: `3px solid ${cc.accent}`,
                                                                pl: 1,
                                                                mb: 0.7,
                                                                opacity: 0.85,
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="caption"
                                                                noWrap
                                                                sx={{
                                                                    display: 'block',
                                                                    color: cc.accent,
                                                                    fontSize: '10px',
                                                                    fontWeight: 700,
                                                                }}
                                                            >
                                                                {quoted.senderName
                                                                    || (quoted.outgoing
                                                                        ? t('crmMultichannel.voce')
                                                                        : conversation.contactName)}
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    display: '-webkit-box',
                                                                    WebkitLineClamp: 2,
                                                                    WebkitBoxOrient: 'vertical',
                                                                    overflow: 'hidden',
                                                                    overflowWrap: 'anywhere',
                                                                    color: cc.textSecondary,
                                                                }}
                                                            >
                                                                {quoted.deleted
                                                                    ? t('crmMultichannel.mensagemExcluida')
                                                                    : quoted.content
                                                                    || t('crmMultichannel.anexo')}
                                                            </Typography>
                                                        </Box>
                                                    )}

                                                    {/* O Chatwoot já entrega a URL do anexo pronta, sem as duas chamadas da Meta. */}
                                                    {!message.deleted && message.attachmentUrl && (
                                                        message.attachmentType === 'audio' ? (
                                                            <MessageAudio
                                                                message={message}
                                                                inboxId={conversation.inboxId}
                                                                cc={cc}
                                                                canTranscribe={ai?.canTranscribe ?? false}
                                                            />
                                                        ) : (message.attachmentType ?? '').includes('image') ? (
                                                            <Box
                                                                component="img"
                                                                src={message.attachmentUrl}
                                                                alt={t('crmMultichannel.anexo')}
                                                                sx={{
                                                                    display: 'block',
                                                                    maxWidth: '100%',
                                                                    maxHeight: 240,
                                                                    borderRadius: '8px',
                                                                    mb: message.content ? 0.7 : 0,
                                                                }}
                                                            />
                                                        ) : (
                                                            <Link
                                                                href={message.attachmentUrl}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                underline="hover"
                                                                sx={{
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    gap: 0.6,
                                                                    mb: message.content ? 0.7 : 0,
                                                                    color: message.outgoing
                                                                        ? cc.textPrimary
                                                                        : cc.accent,
                                                                    fontSize: '0.85rem',
                                                                }}
                                                            >
                                                                <InsertDriveFileIcon sx={{ fontSize: 15 }} />
                                                                {t('crmMultichannel.baixarAnexo')}
                                                            </Link>
                                                        )
                                                    )}

                                                    {message.content && (
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                whiteSpace: 'pre-wrap',
                                                                // Sem isto, um 'kkkkk...' sem espaço estoura o balão e empurra a conversa.
                                                                overflowWrap: 'anywhere',
                                                                color: message.deleted
                                                                    ? cc.textMuted
                                                                    : cc.textPrimary,
                                                                fontStyle: message.deleted
                                                                    ? 'italic'
                                                                    : 'normal',
                                                                lineHeight: 1.45,
                                                            }}
                                                        >
                                                            {message.content}
                                                        </Typography>
                                                    )}
                                                </Box>

                                                {/* Fora do balão: as ações não alargam mais uma mensagem de um caractere. */}
                                                <Box
                                                    className="acoes-da-mensagem"
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.2,
                                                        flexShrink: 0,
                                                        opacity: 0,
                                                        transition: 'opacity .15s',
                                                    }}
                                                >
                                                    {!message.deleted && !blocked && (
                                                        <Tooltip title={t('crmMultichannel.responder')}>
                                                            <IconButton
                                                                onClick={() => setReplyingTo(message)}
                                                                sx={{ p: 0.4, color: cc.textMuted }}
                                                            >
                                                                <ReplyIcon sx={{ fontSize: 15 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}

                                                    {message.outgoing && !message.deleted && (
                                                        <Tooltip title={t('crmMultichannel.excluir')}>
                                                            <IconButton
                                                                onClick={() => remove(message)}
                                                                sx={{ p: 0.4, color: cc.textMuted }}
                                                            >
                                                                <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </Box>
                                        );
                                    })}

                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            px: 0.4,
                                            mt: 0.2,
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            sx={{ color: cc.textMuted, fontSize: '10.5px' }}
                                        >
                                            {formatCrmTime(lastOfGroup.createdAt)}
                                        </Typography>

                                        {/* O tique só faz sentido no que saiu daqui. */}
                                        {group.outgoing && !group.isPrivate && (
                                            <MessageStatusIcon
                                                status={lastOfGroup.status}
                                                failureReason={lastOfGroup.failureReason}
                                            />
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        );
                    })
                )}
                <div ref={endOfList} />
            </Box>

            {blocked && (
                <Alert
                    severity="warning"
                    icon={false}
                    sx={{ mx: 1.5, mb: 1, py: 0.5 }}
                    // Fora do WhatsApp não há template: a única saída é o contato escrever primeiro.
                    action={acceptsTemplate ? (
                        <MDButton
                            variant="text"
                            color="warning"
                            size="small"
                            onClick={() => setTemplatesOpen(true)}
                        >
                            {t('crmMultichannel.usarTemplate')}
                        </MDButton>
                    ) : undefined}
                >
                    <Typography variant="caption">{t('crmMultichannel.bloqueado')}</Typography>
                </Alert>
            )}

            {replyingTo && (
                <Box
                    sx={{
                        mx: 1.5,
                        mb: 0.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        borderLeft: `3px solid ${cc.accent}`,
                        bgcolor: cc.countBg,
                        borderRadius: '6px',
                        p: '6px 8px',
                    }}
                >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                            variant="caption"
                            sx={{
                                display: 'block',
                                color: cc.accent,
                                fontWeight: 700,
                                fontSize: '10px',
                            }}
                        >
                            {t('crmMultichannel.respondendoA')}
                        </Typography>
                        <Typography
                            variant="caption"
                            noWrap
                            sx={{ display: 'block', color: cc.textSecondary }}
                        >
                            {replyingTo.content || t('crmMultichannel.anexo')}
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={() => setReplyingTo(null)}
                        aria-label={t('crmMultichannel.cancelarResposta')}
                        sx={{ p: 0.3, color: cc.textSecondary }}
                    >
                        <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                </Box>
            )}

            {attachment && (
                <Box sx={{ px: 1.5, pb: 0.5 }}>
                    <Chip
                        icon={<InsertDriveFileIcon />}
                        label={attachment.name}
                        size="small"
                        onDelete={() => setAttachment(null)}
                        sx={{ maxWidth: '100%' }}
                    />
                </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, p: 1.5, borderTop: `1px solid ${cc.border}` }}>
                <input
                    ref={fileField}
                    type="file"
                    accept={ACCEPT_ATTR}
                    hidden
                    onChange={(e) => {
                        chooseFile(e.target.files?.[0]);
                        e.target.value = '';
                    }}
                />

                {voice.recording && (
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 40,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.2,
                            px: 1,
                            borderRadius: '10px',
                            border: `1px solid ${cc.border}`,
                            bgcolor: cc.surfaceBg,
                        }}
                    >
                        <Tooltip title={t('crmMultichannel.cancelarGravacao')}>
                            <IconButton onClick={voice.cancel} sx={{ p: 0.5, color: cc.textMuted }}>
                                <DeleteOutlineIcon sx={{ fontSize: 19 }} />
                            </IconButton>
                        </Tooltip>

                        <Box
                            sx={{
                                width: 9,
                                height: 9,
                                borderRadius: '50%',
                                bgcolor: cc.eventDanger,
                                animation: 'gravando 1.2s ease-in-out infinite',
                                '@keyframes gravando': {
                                    '0%, 100%': { opacity: 1 },
                                    '50%': { opacity: 0.25 },
                                },
                            }}
                        />

                        <Typography sx={{ fontSize: 14, color: cc.textPrimary, fontVariantNumeric: 'tabular-nums' }}>
                            {recordingClock(voice.seconds)}
                        </Typography>

                        <Typography variant="caption" sx={{ color: cc.textMuted }}>
                            {t('crmMultichannel.gravando')}
                        </Typography>
                    </Box>
                )}

                {/* Os três ficam dentro do campo: fora dele competiam com o botão de enviar. */}
                {!voice.recording && <TextField
                    ref={fieldRef}
                    inputRef={inputRef}
                    fullWidth
                    size="small"
                    multiline
                    maxRows={4}
                    placeholder={
                        blocked ? t('crmMultichannel.bloqueadoCampo') : t('crmMultichannel.escreva')
                    }
                    value={text}
                    // Só o canal bloqueado desabilita: desabilitar ao enviar tira o foco do campo.
                    disabled={blocked}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                            setOpenReplies(false);
                            return;
                        }

                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();

                            // Enter com o atalho na tela expande, em vez de mandar "/orcamento" cru.
                            if (suggestions.length > 0) {
                                applyReply(suggestions[0].content);
                                return;
                            }

                            send();
                        }
                    }}
                    inputProps={{ maxLength: 4096 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment
                                position="start"
                                sx={{ alignSelf: 'flex-end', mb: '5px', mr: 0.6 }}
                            >
                                <IconButton
                                    onClick={() => fileField.current?.click()}
                                    aria-label={t('crmMultichannel.anexarArquivo')}
                                    disabled={blocked || sending}
                                    sx={{ p: 0.5, color: cc.textMuted }}
                                >
                                    <AttachFileIcon sx={{ fontSize: 17 }} />
                                </IconButton>

                                {aiConfigured && (
                                    <Tooltip title={t('ia.sugerirResposta')}>
                                        <span>
                                            <IconButton
                                                onClick={suggestReply}
                                                disabled={blocked || sending || suggesting}
                                                sx={{ p: 0.5, color: cc.accent }}
                                            >
                                                {suggesting
                                                    ? <CircularProgress size={15} color="inherit" />
                                                    : <AutoAwesomeIcon sx={{ fontSize: 17 }} />}
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                )}

                                <Tooltip title={t('quickReplies.usar')}>
                                    <span>
                                        <IconButton
                                            onClick={() => setOpenReplies((current) => !current)}
                                            disabled={
                                                blocked || sending || quickReplies.length === 0
                                            }
                                            sx={{ p: 0.5, color: cc.textMuted }}
                                        >
                                            <BoltIcon sx={{ fontSize: 17 }} />
                                        </IconButton>
                                    </span>
                                </Tooltip>

                                {acceptsTemplate && (
                                    <Tooltip title={t('crmMultichannel.usarTemplate')}>
                                        <span>
                                            <IconButton
                                                onClick={() => setTemplatesOpen(true)}
                                                disabled={sending}
                                                sx={{ p: 0.5, color: cc.textMuted }}
                                            >
                                                <DescriptionOutlinedIcon sx={{ fontSize: 17 }} />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                )}
                            </InputAdornment>
                        ),
                    }}
                />}
                <Popper
                    open={!voice.recording && (openReplies || suggestions.length > 0)}
                    anchorEl={fieldRef.current}
                    placement="top-start"
                    style={{ zIndex: 1300 }}
                >
                    <ClickAwayListener onClickAway={() => setOpenReplies(false)}>
                        <Paper
                            sx={{
                                mb: 1,
                                width: 380,
                                maxHeight: 300,
                                overflowY: 'auto',
                                bgcolor: cc.menuBg,
                                border: `1px solid ${cc.border}`,
                                borderRadius: '11px',
                                p: 0.6,
                            }}
                        >
                            {(suggestions.length > 0 ? suggestions : quickReplies).map((reply) => (
                                <Box
                                    key={reply.id}
                                    onClick={() => applyReply(reply.content)}
                                    sx={{
                                        px: 1.4,
                                        py: 1,
                                        cursor: 'pointer',
                                        borderRadius: '8px',
                                        '&:hover': { bgcolor: cc.countBg },
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: cc.accent,
                                            fontFamily: 'monospace',
                                            fontWeight: 700,
                                            display: 'block',
                                        }}
                                    >
                                        {reply.shortcut}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: cc.textSecondary,
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                            overflowWrap: 'anywhere',
                                        }}
                                    >
                                        {reply.content}
                                    </Typography>
                                </Box>
                            ))}
                        </Paper>
                    </ClickAwayListener>
                </Popper>

                <IconButton
                    onClick={voice.recording ? sendVoice : canRecord ? startVoice : send}
                    aria-label={canRecord && !voice.recording ? t('crmMultichannel.gravarAudio') : undefined}
                    disabled={
                        sending
                        || (!voice.recording && !canRecord
                            && (blocked || (!attachment && text.trim().length === 0)))
                    }
                    sx={{
                        width: 40,
                        height: 40,
                        flexShrink: 0,
                        borderRadius: '10px',
                        bgcolor: cc.accent,
                        color: '#fff',
                        '&:hover': { bgcolor: cc.accent, filter: 'brightness(1.1)' },
                        '&.MuiIconButton-root.Mui-disabled': {
                            bgcolor: cc.countBg,
                            color: cc.textMuted,
                        },
                    }}
                >
                    {sending
                        ? <CircularProgress size={18} color="inherit" />
                        : canRecord && !voice.recording
                            ? <MicIcon fontSize="small" />
                            : <SendIcon fontSize="small" />}
                </IconButton>
            </Box>

            <TemplateDialog
                open={templatesOpen}
                conversationId={conversationId ?? null}
                inboxId={conversation.inboxId}
                onClose={() => setTemplatesOpen(false)}
                onSent={() => { load(); onSent(); }}
            />
        </Box>
    );
};
